import React, { useEffect, useMemo, useState } from "react";
import { storage } from "../firebase";
import { brandsApi } from "../api/brandsApi";
import type { Brand } from "../types/brands.types"
import { toast } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAdd,
  faEdit,
  faSearch,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { Modal, Box, Typography, Button } from "@mui/material";
import "../screenstyles/Product.css";
import "../screenstyles/CollectionManagement.css";
import {
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { ApiError } from "../api/apiError";


type BrandForm = {
  id: string;
  name: string;
  nameAr: string;
  logoUrl: string | null;
  isActive: boolean;
  sortOrder: number;
};

const emptyBrand: BrandForm = {
  id: "",
  name: "",
  nameAr: "",
logoUrl: null,
  isActive: true,
  sortOrder: 1,
};

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/\+/g, "plus")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function getBrandErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  if (!(error instanceof ApiError)) {
    return fallbackMessage;
  }

  switch (error.code) {
    case "BRAND_ALREADY_EXISTS":
      return "يوجد براند آخر بنفس الـ ID.";

    case "BRAND_NAME_ALREADY_EXISTS":
      return "يوجد براند آخر بنفس الاسم الإنجليزي.";

    case "BRAND_NOT_FOUND":
      return "البراند غير موجود أو تم حذفه.";

    case "BRAND_ALREADY_INACTIVE":
      return "هذا البراند غير مفعل بالفعل.";

    case "FORBIDDEN":
      return "لا تملك الصلاحية المطلوبة لتنفيذ هذه العملية.";

    case "VALIDATION_ERROR":
      return "بيانات البراند غير صحيحة. تحقق من الحقول وحاول مجددًا.";

    default:
      return error.message || fallbackMessage;
  }
}

const Brands: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
const [form, setForm] = useState<BrandForm>(emptyBrand);
const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
const [logoPreviewUrl, setLogoPreviewUrl] = useState<string>("");
const [saving, setSaving] = useState(false);
 

const fetchBrands = async () => {
  setLoading(true);

  try {
    const result = await brandsApi.list({
      sort: "sortOrder_asc",
      limit: 100,
    });

    setBrands(result.items);
  } catch (error) {
    console.error("Error fetching brands from API:", error);
    toast.error("حدث خطأ أثناء تحميل البراندات من الخادم");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchBrands();
  }, []);

  const filteredBrands = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    if (!q) return brands;

    return brands.filter((brand) =>
      [brand.id, brand.name, brand.nameAr, String(brand.sortOrder)]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [brands, searchQuery]);

const openAdd = () => {
  setEditingId(null);
  setSelectedLogoFile(null);
  setLogoPreviewUrl("");

  setForm({
    ...emptyBrand,
    sortOrder: brands.length + 1,
  });

  setFormOpen(true);
};

const openEdit = (brand: Brand) => {
  setEditingId(brand.id);
  setSelectedLogoFile(null);
  setLogoPreviewUrl(brand.logoUrl || "");

  setForm({
    id: brand.id,
    name: brand.name,
    nameAr: brand.nameAr,
    logoUrl: brand.logoUrl,
    isActive: brand.isActive,
    sortOrder: brand.sortOrder,
  });

  setFormOpen(true);
};

 const closeForm = () => {
  setFormOpen(false);
  setEditingId(null);
  setSelectedLogoFile(null);
  setLogoPreviewUrl("");
  setForm(emptyBrand);
};

const handleLogoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  const file = event.target.files?.[0];

  if (!file) return;

  if (!file.type.startsWith("image/")) {
    toast.error("يرجى اختيار ملف صورة فقط");
    return;
  }

  const maxSizeInMB = 2;
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

  if (file.size > maxSizeInBytes) {
    toast.error(`حجم الصورة يجب أن لا يتجاوز ${maxSizeInMB}MB`);
    return;
  }

  setSelectedLogoFile(file);
  setLogoPreviewUrl(URL.createObjectURL(file));
};
const uploadBrandLogo = async (brandId: string) => {
  if (!selectedLogoFile) {
    return form.logoUrl?.trim() || null;
  }

  const fileExtension = selectedLogoFile.name.split(".").pop() || "jpg";

  const logoRef = ref(
    storage,
    `brand-logos/${brandId}/${Date.now()}.${fileExtension}`
  );

  await uploadBytes(logoRef, selectedLogoFile);

  const downloadUrl = await getDownloadURL(logoRef);

  return downloadUrl;
};


const handleSave = async () => {
  const normalizedId =
    editingId || slugify(form.id || form.name);

  const normalizedName = form.name.trim();
  const normalizedNameAr = form.nameAr.trim();
  const normalizedSortOrder = Number(form.sortOrder);

  if (
    !normalizedId ||
    !normalizedName ||
    !normalizedNameAr
  ) {
    toast.error(
      "يرجى إدخال ID والاسم العربي والإنجليزي",
    );
    return;
  }

  if (
    !Number.isInteger(normalizedSortOrder) ||
    normalizedSortOrder < 0
  ) {
    toast.error(
      "الترتيب يجب أن يكون عددًا صحيحًا يساوي صفرًا أو أكثر",
    );
    return;
  }

  try {
    setSaving(true);

    const logoUrl =
      await uploadBrandLogo(normalizedId);

    if (editingId) {
      await brandsApi.update(editingId, {
        name: normalizedName,
        nameAr: normalizedNameAr,
        logoUrl,
        isActive: Boolean(form.isActive),
        sortOrder: normalizedSortOrder,
      });
    } else {
      await brandsApi.create({
        id: normalizedId,
        name: normalizedName,
        nameAr: normalizedNameAr,
        logoUrl,
        isActive: Boolean(form.isActive),
        sortOrder: normalizedSortOrder,
      });
    }

    toast.success(
      editingId
        ? "تم تعديل البراند"
        : "تمت إضافة البراند",
      {
        className: "products-toast-success",
      },
    );

    closeForm();
    await fetchBrands();
  } catch (error) {
    console.error(
      "Error saving brand through API:",
      error,
    );

    toast.error(
      getBrandErrorMessage(
        error,
        "حدث خطأ أثناء حفظ البراند أو رفع الصورة",
      ),
    );
  } finally {
    setSaving(false);
  }
};

  const openDelete = (id: string) => {
    setSelectedId(id);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    setSelectedId(null);
    setDeleteOpen(false);
  };

const handleDelete = async () => {
  if (!selectedId) return;

  try {
    setSaving(true);

    const result =
      await brandsApi.delete(selectedId);

    toast.success(
      result.productsCount > 0
        ? `تم تعطيل البراند، وهو مرتبط بـ ${result.productsCount} منتج`
        : "تم تعطيل البراند",
      {
        className: "products-toast-success",
      },
    );

    closeDelete();
    await fetchBrands();
  } catch (error) {
    console.error(
      "Error deleting brand through API:",
      error,
    );

    toast.error(
      getBrandErrorMessage(
        error,
        "حدث خطأ أثناء تعطيل البراند",
      ),
    );
  } finally {
    setSaving(false);
  }
};

  return (
    <div className="products-page collection-page collection-brands-page">
      <div className="products-header collection-header collection-header-brands">
        <h1 className="products-title collection-title">إدارة البراندات</h1>

        <div className="products-search-box">
          <FontAwesomeIcon icon={faSearch} className="products-search-icon" />
          <input
            type="text"
            placeholder="البحث في البراندات..."
            className="products-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <button
          type="button"
          className="products-header-icon-btn"
          onClick={openAdd}
          title="إضافة براند"
        >
          <FontAwesomeIcon icon={faAdd} />
        </button>
      </div>

      {loading ? (
        <div className="products-loading">Loading...</div>
      ) : (
        <div className="products-table-container">
          <table className="products-table collection-table">
            <thead>
              <tr>
                <th>#</th>
                <th>الشعار</th>
                <th>ID</th>
                <th>الاسم EN</th>
                <th>الاسم AR</th>
                <th>الحالة</th>
                <th>الترتيب</th>
                <th>الإجراءات</th>
              </tr>
            </thead>

            <tbody>
              {filteredBrands.map((brand, index) => (
                <tr key={brand.id}>
                  <td>{index + 1}</td>

                  <td>
                    {brand.logoUrl ? (
                      <img
                        src={brand.logoUrl}
                        alt={brand.name}
                        className="products-image"
                      />
                    ) : (
                      <span className="collection-logo-placeholder">Logo</span>
                    )}
                  </td>

                  <td className="products-cell-text">{brand.id}</td>
                  <td className="products-cell-text">{brand.name}</td>
                  <td className="products-cell-text">{brand.nameAr}</td>

                  <td className="products-cell-text">
                    <span
                      className={
                        brand.isActive
                          ? "collection-badge active"
                          : "collection-badge inactive"
                      }
                    >
                      {brand.isActive ? "مفعل" : "غير مفعل"}
                    </span>
                  </td>

                  <td className="products-cell-text">{brand.sortOrder}</td>

                  <td>
                    <div className="products-actions">
                      <button
                        type="button"
                        className="products-action-btn products-edit-btn"
                        onClick={() => openEdit(brand)}
                        title="تعديل"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>

                      <button
                        type="button"
                        className="products-action-btn products-delete-btn"
                        onClick={() => openDelete(brand.id)}
                      title="تعطيل"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredBrands.length === 0 && (
            <div className="products-empty-state">
              لا توجد براندات مطابقة للبحث.
            </div>
          )}
        </div>
      )}

      <Modal open={formOpen} onClose={closeForm} className="products-modal">
        <Box className="products-modal-card collection-form-card">
          <Typography className="products-modal-title">
            {editingId ? "تعديل براند" : "إضافة براند"}
          </Typography>

          <div className="collection-form-grid">
            <label>
              ID
              <input
                disabled={Boolean(editingId)}
                value={form.id}
                onChange={(e) =>
                  setForm({
                    ...form,
                    id: slugify(e.target.value),
                  })
                }
                placeholder="hikvision"
              />
            </label>

            <label>
              الاسم EN
              <input
                value={form.name}
                onChange={(e) =>
                  setForm({
                    ...form,
                    name: e.target.value,
                    id: editingId ? form.id : slugify(e.target.value),
                  })
                }
                placeholder="HIKVISION"
              />
            </label>

            <label>
              الاسم AR
              <input
                value={form.nameAr}
                onChange={(e) =>
                  setForm({
                    ...form,
                    nameAr: e.target.value,
                  })
                }
                placeholder="هيكفيجن"
              />
            </label>
<label className="collection-logo-upload-field">
  صورة البراند

  <div className="collection-logo-upload-box">
    {logoPreviewUrl ? (
      <img
        src={logoPreviewUrl}
        alt="Brand Logo Preview"
        className="collection-logo-preview"
      />
    ) : (
      <span className="collection-logo-upload-placeholder">
        لا توجد صورة
      </span>
    )}

    <input
      type="file"
      accept="image/*"
      onChange={handleLogoFileChange}
    />
  </div>
</label>
            <label>
              الترتيب
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) =>
                  setForm({
                    ...form,
                    sortOrder: Number(e.target.value),
                  })
                }
              />
            </label>

            <label className="collection-checkbox-label">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) =>
                  setForm({
                    ...form,
                    isActive: e.target.checked,
                  })
                }
              />
              مفعل
            </label>
          </div>

          <Box className="products-modal-actions">
          <Button
  className="collection-save-btn"
  onClick={handleSave}
  disabled={saving}
>
  {saving ? "جاري الحفظ..." : "حفظ"}
</Button>

            <Button className="products-modal-cancel-btn" onClick={closeForm}>
              إلغاء
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={deleteOpen} onClose={closeDelete} className="products-modal">
        <Box className="products-modal-card">
          <Typography className="products-modal-title">
         هل أنت متأكد من تعطيل هذا البراند؟
          </Typography>

          <Box className="products-modal-actions">
            <Button className="products-modal-delete-btn" onClick={handleDelete}>
           نعم، عطّل
            </Button>

            <Button className="products-modal-cancel-btn" onClick={closeDelete}>
              إلغاء
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default Brands;